/**
 * Planches d'étiquettes QR à imprimer (phase 3).
 *
 * La page EST la planche : chaque page A4 est dessinée à sa taille réelle, en
 * millimètres, et l'impression du navigateur — ou « Enregistrer en PDF » — la
 * reproduit telle quelle. Aucune bibliothèque PDF n'est ajoutée pour cela.
 *
 * Deux formats courants de planches prédécoupées, qui remplissent exactement un
 * A4 sans marge : 3 × 8 étiquettes de 70 × 37 mm, ou 2 × 4 de 105 × 74 mm.
 *
 * Le code est aussi imprimé EN CLAIR sous le QR : une étiquette rayée ou
 * graissée reste exploitable par une saisie à la main.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, api, type EtiquetteQr, type PlancheQr } from '../api/client';

const FORMATS = {
  petit: { libelle: 'Petites — 3 × 8 par page (70 × 37 mm)', colonnes: 3, lignes: 8 },
  grand: { libelle: 'Grandes — 2 × 4 par page (105 × 74 mm)', colonnes: 2, lignes: 4 },
} as const;

type Format = keyof typeof FORMATS;

export function EtiquettesQrPage() {
  const [params] = useSearchParams();
  const [planche, setPlanche] = useState<PlancheQr | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [format, setFormat] = useState<Format>('petit');

  const ids = params.get('ids');
  const search = params.get('search');
  const siteId = params.get('site');
  const status = params.get('status');

  useEffect(() => {
    let actif = true;
    setPlanche(null);
    setErreur(null);
    api
      .qrLabels({
        ids: ids ? ids.split(',') : undefined,
        search: search ?? undefined,
        siteId: siteId ?? undefined,
        status: status ?? undefined,
      })
      .then((p) => {
        if (actif) setPlanche(p);
      })
      .catch((e: unknown) => {
        if (actif) setErreur(e instanceof ApiError ? e.message : 'Étiquettes illisibles.');
      });
    return () => {
      actif = false;
    };
  }, [ids, search, siteId, status]);

  const pages = useMemo(() => {
    if (!planche) return [];
    const parPage = FORMATS[format].colonnes * FORMATS[format].lignes;
    const decoupe: EtiquetteQr[][] = [];
    for (let i = 0; i < planche.etiquettes.length; i += parPage) {
      decoupe.push(planche.etiquettes.slice(i, i + parPage));
    }
    return decoupe;
  }, [planche, format]);

  const exemple = planche?.etiquettes[0]?.contenu ?? 'AIR-XXXXX-XXXXX';

  return (
    <>
      {/* Tout ce bloc disparaît à l'impression : seule la planche sort. */}
      <div className="barre-etiquettes">
        <div className="fil-ariane">
          <Link to="/parc">← Parc</Link>
        </div>

        <div className="entete-page">
          <div>
            <h1>Étiquettes QR</h1>
            <p>
              {planche
                ? `${planche.etiquettes.length} étiquette${planche.etiquettes.length > 1 ? 's' : ''} sur ${pages.length} page${pages.length > 1 ? 's' : ''}`
                : erreur
                  ? 'Planche indisponible.'
                  : 'Préparation de la planche…'}
            </p>
          </div>
          <div className="actions">
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value as Format)}
              aria-label="Format des étiquettes"
            >
              {(Object.keys(FORMATS) as Format[]).map((cle) => (
                <option key={cle} value={cle}>
                  {FORMATS[cle].libelle}
                </option>
              ))}
            </select>
            <button
              onClick={() => window.print()}
              disabled={!planche || planche.etiquettes.length === 0}
            >
              Imprimer
            </button>
          </div>
        </div>

        {erreur && <div className="message erreur">{erreur}</div>}

        {planche?.tronquee && (
          <div className="message erreur">
            Seules les {planche.etiquettes.length} premières machines sont préparées, sur{' '}
            {planche.total}. Affinez le filtre du parc pour imprimer le reste.
          </div>
        )}

        <div className="message info">
          Chaque QR contient <strong>le code seul</strong> — par exemple <code>{exemple}</code> —
          sans adresse web : il reste lisible hors ligne et ne dépend d’aucun nom de domaine. Un
          téléphone ordinaire affichera ce texte ; l’application terrain, elle, ouvrira la fiche.
          Le code est aussi imprimé en clair. <strong>Scannez une étiquette d’essai avec un
          téléphone avant d’imprimer en série.</strong>
        </div>
      </div>

      {planche && planche.etiquettes.length === 0 && (
        <div className="vide">Aucune machine ne correspond à cette sélection.</div>
      )}

      <div className={`planches format-${format}`}>
        {pages.map((page, i) => (
          <div key={i} className="page-etiquettes">
            {page.map((e) => (
              <div key={e.assetId} className="etiquette">
                <img className="etiquette-qr" src={e.qrImage} alt={`QR ${e.qrCode}`} />
                <div className="etiquette-texte">
                  <strong className="etiquette-code">{e.qrCode}</strong>
                  <span className="etiquette-designation">{e.label ?? e.model ?? e.assetType}</span>
                  {e.serialNumber && <span>N° {e.serialNumber}</span>}
                  {(e.customerName || e.siteName) && (
                    <span>{[e.customerName, e.siteName].filter(Boolean).join(' · ')}</span>
                  )}
                  {format === 'grand' && e.location && <span>{e.location}</span>}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}
